/**
 * User: Gedas
 * Date: 4/10/15
 * Time: 11:00 AM
 * Copyright 1stdibs.com, Inc. 2015. All Rights Reserved.
 */

'use strict';

// ---------------------------------------------------
// Load Super Class

var BunsenUser = require('bunsen/models/identity/User');

// ---------------------------------------------------
// Module Definition

/**
 * @class User
 * @extends SoaModel
 */
var User = module.exports = BunsenUser.extend();

// ---------------------------------------------------
// Dependencies

var $ = require('dibs-jquery-shim');
var _ = require('underscore');
var guidGenerator = require('../utils/commonjs/guidGenerator');
var storage = require('dibs-browser-storage');
var localStorage = storage.localStorage;
var sessionStorage = storage.sessionStorage;
var defaultCookieSettings = require('bunsen/helpers/dibsCookies').defaultCookieSettings;
var Permission = require('bunsen/models/identity/Permission');
var ContextProvider = require('../reactHelpers/ContextProvider');
var loginActions = require('../actions/loginActions');
var portfolioActions = require('../actions/portfolioActions');
var uri = require('bunsen/helpers/uri');

var contains = require('lodash.contains');
var isEmpty = require('lodash.isempty');

// ---------------------------------------------------
// Local Variables

var root = typeof window === 'undefined' ? global : window;
var deferred = $.Deferred();
var ajaxMade = false;
var emailTokenPromise;
var loginState;

var CUSTOMER_TYPE_VIP = 'VIP';
var CUSTOMER_TYPE_TRADE = 'Trade';
var CUSTOMER_TYPE_CONSUMER = 'Consumer';

require('bunsen/libraries/plugins/jquery.cookie'); // jQuery.fn.cookie

function reloadPage() {
    if (root && root.location) {
        root.location.reload(true);
    }
}

/**
 * Sets initial user related cookies
 */
function setCookies() {
    var currentUser = User.getCurrentUser();

    if (currentUser) {
        $.cookie(User.COOKIE_USER_LEGACY, currentUser.get('legacyCookie'), User.getCookieSettings(182));
        $.cookie(User.COOKIE_USER, currentUser.get('token'), User.getCookieSettings(182));
    }
}

/**
 * Sets additional user related cookies
 */
function setAdditionalCookies() {
    var currentUser = User.getCurrentUser();
    var userType;

    if (currentUser) {
        userType = (currentUser.isVerifiedTradeBuyer() ? CUSTOMER_TYPE_TRADE : CUSTOMER_TYPE_CONSUMER).toLowerCase();

        if ($.cookie(User.COOKIE_USER_TYPE) !== userType) {
            $.cookie(User.COOKIE_USER_TYPE, userType, User.getCookieSettings(182));
            $.cookie(User.COOKIE_EVER_LOGGED_IN, userType, User.getCookieSettings(365 * 5));
        }
    }
}

/**
 * Removes cookies by given arguments/keys
 */
function removeCookies() {
    var length = arguments.length;
    var i;

    for (i = 0; i < length; i++) {
        $.cookie(arguments[i], '', User.getCookieSettings(new Date(-1)));
        $.removeCookie(arguments[i]);
    }
}

/**
 * Initializes User:
 * 1. Sets cookies
 * 2. Triggers login state update
 * 3. Stores data in local storage
 * 4. Manages guest id
 */
function initUser() {
    setCookies();
    User.updateLoginState();
    User.setStorage();

    if (User.getGuestId()) {
        User.getCurrentUser().saveGuestId(User.getGuestId());
        User.removeGuestId();
    }
}

// ---------------------------------------------------
// Define Module Instance Properties

_.extend(User.prototype, {
    initialized: false,

    /**
     * @param {String} email
     * @param {String} password
     * @returns {Promise}
     */
    login: function () {
        return User.__super__.login.apply(this, arguments).done(initUser);
    },

    /**
     * @param {String} email
     * @param {String} password
     * @returns {Promise}
     */
    register: function () {
        var self = this;
        return User.__super__.register.apply(this, arguments)
            .done(function () {
                initUser();
                // Set this flag to identify that the user has just created his account,
                // used in currencyActions.
                self.set('newUser', true);
            });
    },

    /**
     * @param {String} email
     * @param {String} password
     * @param {String} role
     * @returns {Promise}
     */
    registerWithRole: function () {
        return User.__super__.registerWithRole.apply(this, arguments).done(initUser);
    },

    /**
     * @param {String} password
     * @returns {Promise}
     */
    resetPassword: function () {
        return User.__super__.resetPassword.apply(this, arguments).done(initUser);
    },

    /**
     * @param {String} currentPassword
     * @param {String} newPassword
     * @returns {Promise}
     */
    updatePassword: function () {
        var self = this;
        return User.__super__.updatePassword.apply(this, arguments).done(function () {
            self.once('change:token', setCookies);
        });
    }
});

// ---------------------------------------------------
// Define Module Static Properties

_.extend(User, {
    STORAGE_KEY_USER_EMAIL: 'userEmail',
    STORAGE_KEY_USER_EMAIL_TOKEN: 'userEmailToken',

    /**
     * @param {Object} attrs
     * @returns {User}
     * @private
     */
    _createInstance: function (attrs) {
        return new User(attrs);
    },

    /**
     * @param {String} key
     * @returns {User}
     * @private
     */
    _getUser: function (key) {
        var user = BunsenUser._getUser.apply(this, arguments);

        if (!user.initialized) {
            User.prototype.initialized = true;
            user = BunsenUser._getUser.call(this, key, true);

            ContextProvider.getContext().then(function (context) {
                user.once('login:change', function () {
                    context.executeAction(loginActions.handleLoginChange, {user: user});
                });

                context.executeAction(loginActions.handleLoginChange, {user: user});
            });
        }

        return user;
    },

    /**
     * @param {Integer} expires
     * @returns {Object}
     */
    getCookieSettings: function (expires) {
        expires = expires || null;
        return _.extend({}, defaultCookieSettings, {expires: expires});
    },

    /**
     * Sets user params to local storage
     */
    setStorage: function () {
        var user = User.getCurrentUser();

        localStorage.setItem(User.STORAGE_KEY_USER_EMAIL, user.get('profile.email'));
        localStorage.setItem(User.STORAGE_KEY_USER_EMAIL_TOKEN, user.get('emailToken'));
    },

    /**
     * Deletes user params from local storage
     */
    deleteStorage: function () {
        localStorage.deleteItem(User.STORAGE_KEY_USER_EMAIL);
    },

    /**
     * @param {Boolean} isTrade
     */
    setEverLoggedInCookie: function (isTrade) {
        var type = isTrade ? CUSTOMER_TYPE_TRADE : CUSTOMER_TYPE_CONSUMER;
        $.cookie(User.COOKIE_EVER_LOGGED_IN, type.toLowerCase(), this.getCookieSettings(365 * 5));
    },

    /**
     * @returns {Boolean}
     */
    everLoggedInAsTrade: function () {
        return $.cookie(User.COOKIE_EVER_LOGGED_IN) === CUSTOMER_TYPE_TRADE.toLowerCase();
    },

    /**
     * @param {Object} attrs
     */
    updateLoginState: function (attrs) {
        var curState = this.loggedIn();

        if (loginState !== undefined && curState !== loginState) {
            $.publish('login:change', _.extend({}, attrs || {}, {loggedIn: curState}));
            this.getCurrentUser().trigger('login:change');
        }

        loginState = curState;
    },

    /**
     * @param {Boolean} reload
     */
    logout: function (reload) {
        reload = reload || false;

        if (this.isMasquerading()) {
            removeCookies(User.COOKIE_USER_MASQUERADE, User.COOKIE_USER_TYPE);
        } else {
            removeCookies(User.COOKIE_USER, User.COOKIE_USER_LEGACY, User.COOKIE_USER_TYPE);
        }

        if (reload) {
            setTimeout(function () {
                if (root.location.pathname === '/' && root.location.href.indexOf('?modal=reset') !== -1) {
                    // FINDING-2746 - Make this check just on homepage
                    root.location.href = location.origin;
                } else if (root.location.pathname.indexOf('checkout/confirmation') >= 0) {
                    // MONEY-1964 - redirect to home page upon logout from checkout confirmation page
                    root.location.href = location.origin;
                } else {
                    reloadPage();
                }
            }, 100);
        } else {
            this.updateLoginState();
            ajaxMade = false;
        }
    },

    /**
     * @returns {Promise}
     */
    load: function () {
        var self = this;
        var fields = [
            User.FIELD_FULL_PROFILE,
            User.FIELD_CONVERSATION,
            User.FIELD_CART,
            User.FIELD_PERMISSIONS,
            User.FIELD_ADDRESS,
            User.FIELD_ROLES,
            User.FIELD_SOCIAL_NETWORKS
        ];

        // Not logged in, set guest id
        if (!self.loggedIn()) {
            if (!self.getGuestId()) {
                self.setNewGuestId();
            }
            deferred = deferred.state() === 'resolved' ? $.Deferred() : deferred;
            return deferred.reject({type: 'NOT_LOGGED_IN', result: null}).promise();
        }

        // Get user data
        if (!ajaxMade) {
            ajaxMade = true;
            deferred = deferred.state() === 'rejected' ? $.Deferred() : deferred;

            self.getCurrentUserPromise(true, {
                data: {
                    fields: fields.join(',')
                }
            }).done(function () {
                setAdditionalCookies();
                deferred.resolve(self.getCurrentUser().toJSON());
                ContextProvider.getContext().then(function (context) {
                    context.executeAction(portfolioActions.syncLocal);
                });
            }).fail(function (result) {
                // 401 Unauthorized - invalid user token
                if (result && result.status === 401) {
                    self.logout(true);
                }
                deferred.reject({type: 'ERROR', result: result});
            });
        }

        return deferred.promise();
    },

    /**
     * @returns {Object|*}
     */
    getUserProfile: function () {
        return this.getCurrentUser().get('profile');
    },

    /**
     * @param {Object} attrs
     * @returns {Promise}
     */
    updateProfile: function (attrs) {
        return this.getUserProfile().set(attrs).save();
    },

    /**
     * When updating currency need to send just currency, to update other profile values we need 24h fresh token
     *
     * @param {String} currency
     * @returns {Promise}
     */
    updateCurrency: function (currency) {
        var clonedUser = this.getCurrentUser().clone();
        var payload = {currency: currency};

        this.getUserProfile().set(payload); // Update actual profile
        return clonedUser.get('profile').clear().set(payload).save();
    },

    /**
     * @param {Boolean} - useProfileValue true: Use the profile value? defaults to false: localStorage value
     * @returns {String}
     */
    getEmail: function (useProfileValue) {
        return useProfileValue && this.getCurrentUser().get('profile.email') || localStorage.getItem(User.STORAGE_KEY_USER_EMAIL);
    },

    /**
     * @returns {String}
     */
    getEmailToken: function () {
        return localStorage.getItem(User.STORAGE_KEY_USER_EMAIL_TOKEN);
    },

    /**
     * @returns {String}
     */
    getFullName: function () {
        var name = '';
        var profile = this.getUserProfile();
        if (profile) {
            name = (profile.get('firstName') || '') + ' ' + (profile.get('lastName') || '');
            name = $.trim(name);
        }
        return name;
    },

    getCustomerType: function () {
        var customerType = [];
        var permissions = [];

        if (this.getCurrentUser() && this.getCurrentUser().get('permissions.permissionList')) {
            permissions = this.getCurrentUser().get('permissions.permissionList').pluck('name');
        }

        if (contains(permissions, Permission.PERMISSION_B_TRADE_CHECKOUT)) {
            customerType.push(CUSTOMER_TYPE_TRADE);
        }

        if (contains(permissions, Permission.PERMISSION_B_VIP_CHECKOUT)) {
            customerType.push(CUSTOMER_TYPE_VIP);
        }

        if (isEmpty(customerType)) {
            return CUSTOMER_TYPE_CONSUMER;
        }

        return customerType.length ? customerType.join(' and ') : undefined;
    },

    /**
     * @returns {String}
     */
    getZipCode: function () {
        return this.getCurrentUser().get('profile.zipCode');
    },

    /**
     * @returns {string}
     */
    getInitials: function () {
        var initials = '';
        var splitName = _.without(this.getFullName().split(' '), '');

        if (splitName.length > 0) {
            initials = _.reduce(splitName, function (memo, partName) {
                return memo + partName[0];
            }, '');
        }

        return initials;
    },

    /**
     * @returns {String|*}
     */
    getCurrency: function () {
        return this.getCurrentUser() && this.getCurrentUser().get('profile.currency');
    },

    /**
     * @returns {String|*}
     */
    getGuestId: function () {
        return $.cookie(User.COOKIE_GUEST_ID);
    },

    removeGuestId: function () {
        removeCookies(User.COOKIE_GUEST_ID);
    },

    setNewGuestId: function () {
        $.cookie(User.COOKIE_GUEST_ID, guidGenerator.getGuid(), this.getCookieSettings());
    },

    /**
     * Checks to see if the current user
     * has permission to view net/trade prices
     * @returns {boolean}
     */
    canRequestNetPrice: function () {
        var currentUser = this.getCurrentUser();
        return currentUser
            && currentUser.hasTradeBuyerPermission()
            && $.cookie(User.COOKIE_USER_TYPE) === CUSTOMER_TYPE_TRADE.toLowerCase();
    },

    handleEmailToken: function () {
        var self = this;
        var emailToken = uri.URL.queryKey.emailToken;
        var source = uri.URL.queryKey.source;

        if (emailTokenPromise) {
            return emailTokenPromise;
        }

        if (self.loggedIn() || !emailToken) {
            return $.Deferred().resolve().promise();
        }

        emailTokenPromise = self.fetchCurrentUser(true, {
            data: {emailToken: emailToken}
        })
        .done(function () {
            var email = self.getEmail(true);
            var isTrade = source && source.indexOf(CUSTOMER_TYPE_TRADE.toLowerCase()) !== -1;

            if (email) {
                sessionStorage.setItem('email', email);
                localStorage.setItem(User.STORAGE_KEY_USER_EMAIL_TOKEN, emailToken);
                self.setEverLoggedInCookie(isTrade);
            }
        });

        return emailTokenPromise;
    }
});



//////////////////
// WEBPACK FOOTER
// ./assets/js/global/models/User.js
// module id = 15
// module chunks = 62